import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Customer } from '../../types/customer';
import Modal from '../common/Modal';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { useCustomers } from '../../hooks/useCustomers';
import { Combobox } from '@headlessui/react';
import { BuildingOffice2Icon, UserIcon, ChevronUpDownIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface CreateRelationshipLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const CreateRelationshipLogModal: React.FC<CreateRelationshipLogModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { customers, loading } = useCustomers();

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [customerQuery, setCustomerQuery] = useState<string>('');
  const [isOpenTask, setIsOpenTask] = useState<boolean>(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedCustomerId('');
      setContent('');
      setError('');
      setCustomerQuery('');
      setIsOpenTask(false);
    }
  }, [isOpen]);

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!customerQuery.trim()) {
      return customers;
    }
    
    const query = customerQuery.toLowerCase();
    return customers.filter(customer => {
      const nameToMatch = customer.type === 'organization'
        ? customer.companyName.toLowerCase()
        : `${customer.lastName} ${customer.firstName}`.toLowerCase();
      
      const taxNumber = customer.taxNumber?.toLowerCase() || '';
      const email = customer.email?.toLowerCase() || '';
      
      return nameToMatch.includes(query) || 
             taxNumber.includes(query) || 
             email.includes(query);
    });
  }, [customers, customerQuery]);

  const maxLength = 1000;
  
  // Function to generate the entry ID based on requirements
  const generateEntryId = (customer: Customer) => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;
    
    // Calculate sequence number (format as 4 digits)
    const seqNum = String(1).padStart(4, '0'); // Default to 0001
    
    // Determine prefix (first 8 chars of tax number or customer ID)
    let prefix = '';
    if (customer.type === 'organization' && customer.taxNumber) {
      prefix = customer.taxNumber.substring(0, 8);
    } else {
      prefix = customer.id.substring(0, 8);
    }
    
    return `${prefix}_${dateStr}_${seqNum}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCustomerId) {
      setError(t('customers.relationshipLog.customerRequired') || 'Please select a customer');
      return;
    }
    
    if (!content.trim()) {
      setError(t('customers.relationshipLog.contentRequired') || 'Content is required');
      return;
    }

    if (content.length > maxLength) {
      setError(t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`);
      return;
    }

    if (!user) {
      setError(t('common.messages.authContext.error') || 'Authentication error');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
      if (!selectedCustomer) {
        throw new Error('Customer not found');
      }

      // Check if the customer already has logs for today to get the correct sequence number
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      
      const todayLogs = selectedCustomer.relationshipLogs?.filter(log => {
        const logDate = log.createdAt instanceof Date 
          ? log.createdAt 
          : log.createdAt.toDate();
        return logDate >= todayStart && logDate < todayEnd;
      }) || [];
      
      // Calculate sequence number (todayLogs.length + 1, formatted as 4 digits)
      const seqNum = String(todayLogs.length + 1).padStart(4, '0');
      
      // Determine prefix (first 8 chars of tax number or customer ID)
      let prefix = '';
      if (selectedCustomer.type === 'organization' && selectedCustomer.taxNumber) {
        prefix = selectedCustomer.taxNumber.substring(0, 8);
      } else {
        prefix = selectedCustomer.id.substring(0, 8);
      }
      
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const entryId = `${prefix}_${dateStr}_${seqNum}`;

      // Create the log entry
      const newLogEntry = {
        id: uuidv4(),
        customerId: selectedCustomerId,
        entryId,
        content,
        createdAt: Timestamp.now(),
        createdBy: user.uid,
        createdByName: user.displayName || undefined,
        isOpen: isOpenTask
      };
      
      // Update the customer document
      const customerRef = doc(db, 'customers', selectedCustomerId);
      await updateDoc(customerRef, {
        relationshipLogs: arrayUnion(newLogEntry)
      });
      
      toast.success(t('customers.relationshipLog.addSuccess') || 'Log entry added successfully');
      
      // Reset form and close modal
      setSelectedCustomerId('');
      setContent('');
      setIsOpenTask(false);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to add log entry:', err);
      setError(err instanceof Error ? err.message : String(err));
      toast.error(t('customers.relationshipLog.addError') || 'Failed to add log entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('customers.relationshipLog.add') || 'Add new log entry'}
      size="3xl"
    >
      <div className="p-10">
        <form onSubmit={handleSubmit}>
          {/* Customer select with Combobox */}
          <div className="mb-8">
            <label htmlFor="customer-select" className="block text-base font-medium text-gray-700 mb-3">
              {t('customers.title') || 'Customer'} <span className="text-red-500">*</span>
            </label>
            <Combobox
              value={selectedCustomerId}
              onChange={setSelectedCustomerId}
              disabled={isSubmitting || loading}
            >
              <div className="relative">
                <div className="relative w-full">
                  <Combobox.Input
                    className="w-full bg-white border border-gray-300 rounded-lg px-5 py-4 text-base text-gray-800 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    onChange={(event) => setCustomerQuery(event.target.value)}
                    displayValue={(customerId: string) => {
                      const customer = customers.find(c => c.id === customerId);
                      if (!customer) return '';
                      return customer.type === 'organization'
                        ? customer.companyName || ''
                        : `${customer.lastName} ${customer.firstName}`;
                    }}
                    placeholder={t('common.placeholders.selectCustomer') || 'Select customer...'}
                  />
                  <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-4">
                    <ChevronUpDownIcon
                      className="h-6 w-6 text-gray-500"
                      aria-hidden="true"
                    />
                  </Combobox.Button>
                </div>
                <Combobox.Options
                  className="absolute z-50 mt-1 max-h-80 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg 
                           ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  {filteredCustomers.length === 0 && customerQuery !== '' ? (
                    <div className="relative cursor-default select-none py-4 px-4 text-gray-500">
                      {t('common.noData') || 'No customers found'}
                    </div>
                  ) : (
                    filteredCustomers.map(customer => (
                      <Combobox.Option
                        key={customer.id}
                        value={customer.id}
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-4 px-5 ${active ? 'bg-primary-100 text-primary-900' : 'text-gray-800'
                          }`
                        }
                      >
                        {({ selected, active }) => (
                          <div className="flex items-center space-x-5">
                            <div className={`p-2 rounded ${customer.type === 'organization'
                              ? 'bg-blue-100'
                              : 'bg-purple-100'
                              }`}>
                              {customer.type === 'organization' ? (
                                <BuildingOffice2Icon className="h-6 w-6 text-blue-600" />
                              ) : (
                                <UserIcon className="h-6 w-6 text-purple-600" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-lg">
                                {customer.type === 'organization'
                                  ? customer.companyName
                                  : `${customer.lastName} ${customer.firstName}`}
                              </div>
                              <div className="text-base text-gray-500">
                                {customer.type === 'organization' && customer.taxNumber
                                  ? customer.taxNumber
                                  : customer.email}
                              </div>
                            </div>
                          </div>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </div>
            </Combobox>
          </div>
          
          {/* Content textarea - increase rows */}
          <div className="mb-8">
            <label htmlFor="log-content" className="block text-base font-medium text-gray-700 mb-3">
              {t('customers.relationshipLog.content') || 'Content'} <span className="text-red-500">*</span>
            </label>
            <textarea
              id="log-content"
              rows={10}
              className={`w-full rounded-lg border ${
                error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              } shadow-sm p-5 text-base`}
              placeholder={t('customers.relationshipLog.contentPlaceholder') || 'Enter log content...'}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={maxLength}
              disabled={isSubmitting}
            />
            <div className="flex justify-between mt-2">
              {error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('customers.relationshipLog.maxLength') || `Maximum ${maxLength} characters`}
                </p>
              )}
              <p className="text-sm text-gray-500">
                {content.length}/{maxLength}
              </p>
            </div>
          </div>
          
          {/* isOpen checkbox for pending tasks */}
          <div className="mb-8">
            <div className="flex items-center">
              <input
                id="isOpenTask"
                name="isOpenTask"
                type="checkbox"
                className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={isOpenTask}
                onChange={(e) => setIsOpenTask(e.target.checked)}
                disabled={isSubmitting}
              />
              <label htmlFor="isOpenTask" className="ml-3 flex items-center text-base font-medium text-gray-700">
                <ExclamationCircleIcon className="h-5 w-5 text-amber-500 mr-2" />
                {t('customers.relationshipLog.isOpenTask') || 'Mark as having pending tasks'}
              </label>
            </div>
            <p className="mt-1 ml-8 text-sm text-gray-500">
              {t('customers.relationshipLog.isOpenTaskDescription') || 'Check this if there are pending tasks related to this log entry'}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-10">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              {t('customers.relationshipLog.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              className="px-8 py-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? t('common.submitting') || 'Saving...'
                : t('customers.relationshipLog.save') || 'Save entry'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default CreateRelationshipLogModal; 